/* BFCM rewards bar
 * Visual only: reads the cart and shows progress toward each milestone.
 * Listens for any cart change (theme, quick add, apps) by watching fetch/XHR
 * calls to /cart/add|change|update|clear, then re-reads /cart.js.
 * On product pages, "Add to Cart" clicks the theme's own main add-to-cart button
 * (so variants, subscriptions, quantity and apps keep working) without opening
 * the drawer, and "Checkout" opens the cart drawer (adding the product first if
 * the cart is empty).
 */
(() => {
  if (customElements.get('bfcm-rewards-bar')) return;

  const EVENT = 'brb:cart-changed';
  const COLLAPSE_KEY = 'brb-collapsed';
  const CART_PATH = /\/cart\/(add|change|update|clear)(\.js|\.json)?$/;
  const DESKTOP = window.matchMedia('(min-width: 990px)');

  const isCartMutation = (url) => {
    try {
      return CART_PATH.test(new URL(url, window.location.origin).pathname);
    } catch (e) {
      return false;
    }
  };

  let notifyTimer;
  const notify = () => {
    clearTimeout(notifyTimer);
    notifyTimer = setTimeout(() => document.dispatchEvent(new CustomEvent(EVENT)), 120);
  };

  // Watch fetch
  const originalFetch = window.fetch;
  window.fetch = function (input) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const request = originalFetch.apply(this, arguments);
    if (isCartMutation(url)) {
      request.then((res) => res.ok && notify()).catch(() => {});
    }
    return request;
  };

  // Watch XHR (jQuery-based apps)
  const originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (isCartMutation(url)) {
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300) notify();
      });
    }
    return originalOpen.apply(this, arguments);
  };

  const shopRoot = () => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const escapeHtml = (str) =>
    String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const formatMoney = (cents, format) => {
    const fmt = format || '${{amount}}';
    const value = (Number(cents) || 0) / 100;
    const withDelimiters = (num, decimals, thousands, decimal) => {
      const [whole, frac] = num.toFixed(decimals).split('.');
      const wholeStr = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? wholeStr + decimal + frac : wholeStr;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(value, 2, '.', ',').replace(/,00$/, '');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(value, 0, '.', ',');
        default:
          return withDelimiters(value, 2, ',', '.').replace(/\.00$/, ''); // match money_without_trailing_zeros
      }
    });
  };

  // ---- Shared rendering (footer bar + cart drawer copy) ----
  const RENDERED = 'brb:rendered';

  const queryView = (root) => ({
    top: root.querySelector('.brb__top'),
    actions: root.querySelector('.brb__actions'),
    progress: root.querySelector('.brb__progress'),
    headline: root.querySelector('.brb__headline'),
    badge: root.querySelector('.brb__badge'),
    totalWrap: root.querySelector('.brb__total'),
    original: root.querySelector('[data-brb-original]'),
    total: root.querySelector('[data-brb-total]'),
    pillTotal: root.querySelector('[data-brb-pill-total]'),
    message: root.querySelector('[data-brb-message]'),
    track: root.querySelector('[data-brb-track]'),
    fill: root.querySelector('[data-brb-fill]'),
    dots: Array.from(root.querySelectorAll('[data-brb-dot]')),
  });

  const renderView = (view, cart, config) => {
    const { milestones, moneyFormat } = config;
    const total = Number(cart.total_price) || 0;
    const original = Number(cart.original_total_price) || total;
    const money = (c) => formatMoney(c, moneyFormat);

    // Totals
    if (view.total) view.total.textContent = money(total);
    if (view.pillTotal) view.pillTotal.textContent = money(total);
    if (view.original) {
      if (original > total) {
        view.original.textContent = money(original);
        view.original.hidden = false;
      } else {
        view.original.hidden = true;
      }
    }

    // Message
    const next = milestones.find((m) => total < m.amount);
    if (view.message) {
      view.message.innerHTML = next
        ? (config.message || '')
            .replace('[amount]', escapeHtml(money(next.amount - total)))
            .replace('[reward]', escapeHtml(next.reward || ''))
        : config.successMessage || '';
    }

    // Progress: evenly spaced segments between milestones
    const last = Number(config.lastMarkerPosition) || 100;
    const count = milestones.length;
    let pct = 100;
    if (next) {
      const i = milestones.indexOf(next);
      const fromAmount = i === 0 ? 0 : milestones[i - 1].amount;
      const fromPos = (last * i) / count;
      const toPos = (last * (i + 1)) / count;
      const span = next.amount - fromAmount || 1;
      pct = fromPos + ((total - fromAmount) / span) * (toPos - fromPos);
    }
    pct = Math.max(0, Math.min(100, pct));
    if (view.fill) view.fill.style.width = `${pct}%`;
    if (view.track) view.track.setAttribute('aria-valuenow', String(Math.round(pct)));
    view.dots.forEach((dot, i) => {
      dot.classList.toggle('is-reached', Boolean(milestones[i] && total >= milestones[i].amount));
    });
  };

  // Hide the divider if the badge and total don't fit on one line (narrow screens)
  const checkWrapView = (view) => {
    const { headline, badge, totalWrap } = view;
    if (!headline || !badge || !totalWrap || !headline.offsetParent) return;
    headline.classList.remove('is-wrapped');
    if (totalWrap.offsetTop > badge.offsetTop + 4) headline.classList.add('is-wrapped');
  };

  class BfcmRewardsBar extends HTMLElement {
    connectedCallback() {
      const configEl = this.querySelector('[data-brb-config]');
      if (!configEl) return;
      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        return;
      }

      this.hasActions = this.classList.contains('brb--has-actions');
      this.panel = this.querySelector('.brb__panel');
      this.pill = this.querySelector('[data-brb-expand]');
      this.closeBtn = this.querySelector('[data-brb-collapse]');
      this.spacer = this.nextElementSibling && this.nextElementSibling.matches('[data-brb-spacer]') ? this.nextElementSibling : null;
      this.els = queryView(this);
      this.itemCount = Number(this.config.cart.item_count) || 0;

      this.closeBtn.addEventListener('click', () => this.setCollapsed(!this.collapsed));
      this.pill.addEventListener('click', () => this.setCollapsed(false));

      this.onCartChanged = () => this.refresh();
      document.addEventListener(EVENT, this.onCartChanged);
      this.onPageShow = (e) => e.persisted && this.refresh();
      window.addEventListener('pageshow', this.onPageShow);
      this.onBreakpoint = () => this.layout();
      DESKTOP.addEventListener('change', this.onBreakpoint);

      if (this.hasActions) this.setupActions();
      this.watchDrawer();
      this.watchOffset();
      this.watchSize();

      // Mobile always opens expanded; desktop remembers the shopper's choice
      let collapsed = false;
      if (DESKTOP.matches) {
        try {
          collapsed = sessionStorage.getItem(COLLAPSE_KEY) === '1';
        } catch (e) {}
      }

      this.render(this.config.cart);
      this.setCollapsed(collapsed, false);

      if (!this.hidden && (!collapsed || this.hasActions)) {
        this.classList.add('brb--entering');
        requestAnimationFrame(() => requestAnimationFrame(() => this.classList.remove('brb--entering')));
      }
    }

    disconnectedCallback() {
      document.removeEventListener(EVENT, this.onCartChanged);
      window.removeEventListener('pageshow', this.onPageShow);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      DESKTOP.removeEventListener('change', this.onBreakpoint);
      if (this.drawerObserver) this.drawerObserver.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.atcObserver) this.atcObserver.disconnect();
      document.documentElement.style.removeProperty('--brb-height');
    }

    async refresh() {
      try {
        const res = await originalFetch(`${shopRoot()}cart.js`, { headers: { Accept: 'application/json' } });
        if (res.ok) this.render(await res.json());
      } catch (e) {}

      if (this.atcPending) {
        this.atcPending = false;
        clearTimeout(this.atcTimer);
        this.setAtcLoading(false);
        // Give the theme a moment to finish its own drawer update before restoring open()
        setTimeout(() => this.restoreDrawer(), 400);
        this.showToast();
      }
    }

    render(cart) {
      this.itemCount = Number(cart.item_count) || 0;
      this.hidden = Boolean(this.config.hideWhenEmpty && !this.itemCount && !this.hasActions);
      renderView(this.els, cart, this.config);
      document.dispatchEvent(new CustomEvent(RENDERED, { detail: cart }));
      this.layout();
    }

    setCollapsed(collapsed, persist = true) {
      this.collapsed = collapsed;
      this.classList.toggle('brb--collapsed', collapsed);
      this.closeBtn.setAttribute('aria-expanded', String(!collapsed));
      this.closeBtn.setAttribute('aria-label', collapsed ? 'Show rewards progress' : 'Minimise');

      // Product pages keep the panel (with its buttons); other pages swap to a pill
      const usePill = collapsed && !this.hasActions;
      this.panel.hidden = usePill;
      this.pill.hidden = !usePill;

      if (persist) {
        try {
          sessionStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
        } catch (e) {}
        (usePill ? this.pill : this.closeBtn).focus({ preventScroll: true });
      }
      this.layout();
    }

    // Recalculate everything that depends on size
    layout() {
      this.checkWrap();
      this.updateShape();
      this.updateSpace();
    }

    // Hide the divider if the badge and total don't fit on one line (narrow phones)
    checkWrap() {
      if (this.hidden || this.panel.hidden) return;
      checkWrapView(this.els);
    }

    // Desktop: tab sized to its content, with a smooth S-curve down to the strip
    updateShape() {
      if (!DESKTOP.matches) {
        this.panel.style.removeProperty('clip-path');
        return;
      }
      if (this.hidden || this.panel.hidden) return;
      const panelRect = this.panel.getBoundingClientRect();
      const parts = [this.els.top, this.els.actions, this.closeBtn].filter((el) => el && el.offsetParent !== null);
      const right = Math.max(...parts.map((el) => el.getBoundingClientRect().right));
      const progressShown = this.els.progress && getComputedStyle(this.els.progress).display !== 'none';
      const W = Math.round(panelRect.width);
      const H = Math.round(panelRect.height);
      const w = Math.ceil(right - panelRect.left + 8);
      const h = Math.round(progressShown ? this.els.progress.getBoundingClientRect().top - panelRect.top : H);
      const curve = 84; // horizontal length of the curved edge
      const r = 16; // top-left corner radius
      this.panel.style.setProperty('--brb-tab-w', `${w}px`);
      this.panel.style.setProperty('--brb-tab-h', `${h}px`);
      this.panel.style.clipPath =
        `path('M0 ${r} Q0 0 ${r} 0 L${w} 0 ` +
        `C${w + curve * 0.55} 0 ${w + curve * 0.45} ${h} ${w + curve} ${h} ` +
        `L${W} ${h} L${W} ${H} L0 ${H} Z')`;
    }

    // Reserve space at the bottom of the page and lift floating buttons above the bar
    updateSpace() {
      let h = 0;
      if (!this.hidden && !this.panel.hidden) h = this.panel.offsetHeight;
      if (this.spacer) this.spacer.style.setProperty('--brb-space', `${h}px`);
      document.documentElement.style.setProperty('--brb-height', `${h + (this.offset || 0)}px`);
    }

    // Product page buttons
    setupActions() {
      this.atcBtn = this.querySelector('[data-brb-atc]');
      this.atcLabel = this.querySelector('[data-brb-atc-label]');
      this.atcDefaultLabel = this.atcLabel ? this.atcLabel.textContent : '';
      this.checkoutBtn = this.querySelector('[data-brb-checkout]');
      this.mainAtc = document.querySelector('.main-product-atc') || document.querySelector('product-form [name="add"]');

      if (!this.mainAtc) {
        // No product form on this page: keep Checkout, drop Add to Cart
        if (this.atcBtn) this.atcBtn.hidden = true;
      } else {
        this.atcBtn.addEventListener('click', () => {
          if (this.mainAtc.disabled || this.atcPending) return;
          this.atcPending = true;
          this.setAtcLoading(true);
          this.suppressDrawer();
          this.mainAtc.click();
          clearTimeout(this.atcTimer);
          // Nothing was added (e.g. validation error): undo after a while
          this.atcTimer = setTimeout(() => {
            this.atcPending = false;
            this.setAtcLoading(false);
            this.restoreDrawer();
          }, 6000);
        });
        const syncAvailability = () => {
          const unavailable = this.mainAtc.disabled && this.mainAtc.hasAttribute('data-unavailable');
          this.atcBtn.disabled = unavailable;
          this.atcLabel.textContent = unavailable ? this.config.soldOutLabel || this.atcDefaultLabel : this.atcDefaultLabel;
        };
        this.atcObserver = new MutationObserver(syncAvailability);
        this.atcObserver.observe(this.mainAtc, { attributes: true, attributeFilter: ['disabled', 'data-unavailable'] });
        syncAvailability();
      }

      // "Checkout" opens the cart drawer
      this.checkoutBtn.addEventListener('click', (event) => {
        event.preventDefault();
        // Keep the theme's "click outside closes the drawer" handler from seeing this click
        event.stopPropagation();
        if (this.itemCount === 0 && this.mainAtc && !this.mainAtc.disabled) {
          // Empty cart: add the selected product; the theme opens the drawer itself
          this.mainAtc.click();
          return;
        }
        setTimeout(() => this.openDrawer(), 0);
      });
    }

    setAtcLoading(loading) {
      if (!this.atcBtn) return;
      this.atcBtn.classList.toggle('is-loading', loading);
      this.atcBtn.setAttribute('aria-busy', String(loading));
    }

    // Stop the theme opening the cart drawer for adds made from this bar.
    // The drawer's contents still update; only open() is skipped.
    suppressDrawer() {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function' || this.drawerOpen) return;
      this.drawer = drawer;
      this.drawerOpen = drawer.open;
      drawer.open = () => {};
    }

    restoreDrawer() {
      if (this.drawer && this.drawerOpen) {
        this.drawer.open = this.drawerOpen;
      }
      this.drawerOpen = null;
    }

    openDrawer() {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      } else {
        window.location.href = `${shopRoot()}cart`;
      }
    }

    showToast() {
      if (!this.toast) {
        const toast = document.createElement('button');
        toast.type = 'button';
        toast.className = 'brb-toast';
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');
        toast.setAttribute('style', this.getAttribute('style') || '');
        toast.innerHTML =
          '<span class="brb-toast__icon" aria-hidden="true"><svg viewBox="0 0 12 12" width="12" height="12"><path d="M2.5 6.5l2.2 2.2L9.5 3.8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>' +
          `<span class="brb-toast__text">${escapeHtml(this.config.toastText || 'Added to cart')}</span>` +
          '<svg class="brb-toast__arrow" viewBox="0 0 12 12" width="10" height="10" aria-hidden="true"><path d="M4 2l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        toast.addEventListener('click', (event) => {
          // Keep the theme's "click outside closes the drawer" handler from seeing this click
          event.stopPropagation();
          this.hideToast();
          setTimeout(() => this.openDrawer(), 0);
        });
        toast.addEventListener('mouseenter', () => clearTimeout(this.toastTimer));
        toast.addEventListener('mouseleave', () => {
          this.toastTimer = setTimeout(() => this.hideToast(), 1500);
        });
        document.body.appendChild(toast);
        this.toast = toast;
      }
      clearTimeout(this.toastTimer);
      requestAnimationFrame(() => this.toast.classList.add('is-visible'));
      this.toastTimer = setTimeout(() => this.hideToast(), 3000);
    }

    hideToast() {
      clearTimeout(this.toastTimer);
      if (this.toast) this.toast.classList.remove('is-visible');
    }

    // Hide while the theme's cart drawer is open
    watchDrawer() {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      const sync = () => this.classList.toggle('brb--drawer-open', drawer.classList.contains('active'));
      this.drawerObserver = new MutationObserver(sync);
      this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      sync();
    }

    // Sit above the theme's sticky add-to-cart when it's showing (when not replaced)
    watchOffset() {
      let ticking = false;
      const update = () => {
        ticking = false;
        let offset = 0;
        const atc = this.hasActions ? null : document.querySelector('sticky-atc');
        if (atc) {
          const rect = atc.getBoundingClientRect();
          const style = getComputedStyle(atc);
          const visible = rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) > 0;
          if (visible && rect.top < window.innerHeight && rect.bottom >= window.innerHeight - 2) {
            offset = Math.max(0, Math.round(window.innerHeight - rect.top));
          }
        }
        if (offset !== this.offset) {
          this.offset = offset;
          this.style.setProperty('--brb-offset', `${offset}px`);
          this.updateSpace();
        }
      };
      this.onScroll = () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      update();
    }

    watchSize() {
      if (!('ResizeObserver' in window)) return;
      this.resizeObserver = new ResizeObserver(() => this.layout());
      this.resizeObserver.observe(this.panel);
    }
  }

  customElements.define('bfcm-rewards-bar', BfcmRewardsBar);

  /* Cart drawer copy: <bfcm-rewards-inline data-total data-original data-count>
   * Clones the footer bar's markup (without buttons/close) and uses its settings,
   * so milestones, text and colours are edited in one place.
   */
  class BfcmRewardsInline extends HTMLElement {
    connectedCallback() {
      const host = document.querySelector('bfcm-rewards-bar');
      const configEl = host && host.querySelector('[data-brb-config]');
      const inner = host && host.querySelector('.brb__inner');
      if (!configEl || !inner) {
        this.hidden = true;
        return;
      }
      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        this.hidden = true;
        return;
      }

      if (!this.querySelector('.brb__panel')) {
        // Inherit the bar's colour variables, keep this block's own margins
        this.style.cssText = `${host.getAttribute('style') || ''};${this.style.cssText}`;
        const panel = document.createElement('div');
        panel.className = 'brb__panel';
        const clone = inner.cloneNode(true);
        clone.querySelectorAll('.brb__actions, .brb__close').forEach((el) => el.remove());
        clone.querySelectorAll('[aria-live]').forEach((el) => el.removeAttribute('aria-live'));
        clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => el.removeAttribute('data-shopify-editor-block'));
        panel.appendChild(clone);
        this.appendChild(panel);
      }

      this.view = queryView(this);
      renderView(
        this.view,
        {
          total_price: this.dataset.total,
          original_total_price: this.dataset.original,
          item_count: this.dataset.count,
        },
        this.config
      );

      this.onRendered = (event) => {
        renderView(this.view, event.detail, this.config);
        checkWrapView(this.view);
      };
      document.addEventListener(RENDERED, this.onRendered);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => checkWrapView(this.view));
        this.resizeObserver.observe(this);
      }
    }

    disconnectedCallback() {
      document.removeEventListener(RENDERED, this.onRendered);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }
  }

  customElements.define('bfcm-rewards-inline', BfcmRewardsInline);
})();
